/**
 * useEngagement — Persist repost & share counts via /api/engagement.
 * Generates a stable card_key from dish name + restaurant (or post_id).
 */
import { useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// MD5-like hash for card key (matches backend logic)
function makeCardKey(dishName = '', restaurant = '', postId = '') {
  if (postId) return postId;
  const raw = `${dishName.trim().toLowerCase()}|${restaurant.trim().toLowerCase()}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = ((hash << 5) - hash + raw.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

// Batch fetcher — collects keys and fetches in one request
const pendingKeys = new Set();
const batchCallbacks = new Map();
let batchTimer = null;

function scheduleBatchFetch() {
  if (batchTimer) return;
  batchTimer = setTimeout(async () => {
    const keys = [...pendingKeys];
    pendingKeys.clear();
    batchTimer = null;
    if (!keys.length) return;

    try {
      const res = await fetch(`${API_URL}/api/engagement/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keys }),
      });
      if (res.ok) {
        const { counts } = await res.json();
        for (const key of keys) {
          const cbs = batchCallbacks.get(key) || [];
          cbs.forEach(cb => cb(counts[key] || null));
          batchCallbacks.delete(key);
        }
      }
    } catch {}
  }, 100); // 100ms debounce to batch multiple cards
}

export function useEngagement(dishName = '', restaurant = '', postId = '', userId = '') {
  const cardKey = useRef(makeCardKey(dishName, restaurant, postId)).current;

  const [repostCount, setRepostCount] = useState(0);
  const [reposted, setReposted] = useState(false);
  const [shareCount, setShareCount] = useState(0);

  // Fetch initial counts via batch
  useEffect(() => {
    pendingKeys.add(cardKey);
    const callbacks = batchCallbacks.get(cardKey) || [];
    callbacks.push((data) => {
      if (data) {
        setRepostCount(data.repost_count || 0);
        setShareCount(data.share_count || 0);
        if (userId && data.reposted_by?.includes(userId)) {
          setReposted(true);
        }
      }
    });
    batchCallbacks.set(cardKey, callbacks);
    scheduleBatchFetch();
  }, [cardKey, userId]);

  const toggleRepost = useCallback(async () => {
    // Optimistic update
    const wasReposted = reposted;
    setReposted(!wasReposted);
    setRepostCount(c => wasReposted ? Math.max(0, c - 1) : c + 1);

    try {
      const res = await fetch(`${API_URL}/api/engagement/repost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ card_key: cardKey, dish_name: dishName, restaurant, post_id: postId, user_id: userId }),
      });
      if (res.ok) {
        const data = await res.json();
        setReposted(data.reposted);
        setRepostCount(data.repost_count);
      }
    } catch {
      // Revert on error
      setReposted(wasReposted);
      setRepostCount(c => wasReposted ? c + 1 : Math.max(0, c - 1));
    }
  }, [cardKey, dishName, restaurant, postId, userId, reposted]);

  const recordShare = useCallback(async (platform) => {
    setShareCount(c => c + 1); // Optimistic

    try {
      const res = await fetch(`${API_URL}/api/engagement/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ card_key: cardKey, dish_name: dishName, restaurant, post_id: postId, platform }),
      });
      if (res.ok) {
        const data = await res.json();
        setShareCount(data.share_count);
      }
    } catch {}
  }, [cardKey, dishName, restaurant, postId]);

  return { cardKey, repostCount, reposted, shareCount, toggleRepost, recordShare };
}
